import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, message, Image, Select, Spin, Empty, Modal, ConfigProvider } from "antd";
import Header from "../../../component/menu/Header";
import SideMenu from "../../../component/menu/SideMenu";
import { constants, INVENTORY_RECIPE_TYPE, ROLES } from "../../../constant/constants";
import InventoryTable from "../../../component/inventory/InventoryTable";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import { prepareRecipeList, getRecipeTypeValue } from "../../../services/inventory/recipe.service";
import InventoryConfirmModal from "../../../component/inventory/InventoryConfirmModal";
import CustomLoader from "../../../component/menu/CustomLoader";
import InventoryDependencyModal from "../../../component/inventory/InventoryDependencyModal";

export default function RecipeList() {
  const [modal, contextHolder] = Modal.useModal();
  const { restaurantId, user, showNotification, role, setNetworkCall } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const recipeTypeFromParams = searchParams.get("recipeType");
  const [searchText, setSearchText] = useState("");
  const [recipeType, setRecipeType] = useState(recipeTypeFromParams || INVENTORY_RECIPE_TYPE[0].value);
  const [categoryOptions, setCategoryOptions] = useState();
  const [category, setCategory] = useState();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [recipeLoading, setRecipeLoading] = useState(true);
  const [recipes, setRecipes] = useState();
  const [selectedRecipeDeleteObj, setSelectedRecipeDeleteObj] = useState({ recipeId: "", recipeType: "" });
  const [openDelModal, setOpenDelModal] = useState(false);
  const [openStatusConfirmModal, setOpenStatusConfirmModal] = useState(false);
  const [changeStatusObj, setChangeStatusObj] = useState({
    recipeType: "",
    recipeId: "",
    optionId: "",
    status: "", // Active / Inactive
  });
  const [openStatusDependencyModal, setOpenStatusDependencyModal] = useState(false);
  const [dependencyObj, setDependencyObj] = useState({
    dependentFoods: [],
    dependentOrders: [],
    dependentOrders: [],
  });
  const [openDelDependencyModal, setOpenDelDependencyModal] = useState(false);

  function onHandlePageChange(page, pageSize) {
    setPage(page);
    setPerPage(pageSize);

    const params = {
      restaurantId,
      recipeType: recipeType,
      searchKey: searchText.trim(),
      categoryId: category || "",
      status: "",
      page: page,
      perPage: pageSize,
    };

    getRecipeList(params);
  }

  async function getCategories() {
    const params = {
      restaurantId: restaurantId,
    };
    try {
      const res = await ApiService.GET_CATEGORY(params, user.token.token);

      if (!res || (res && res.code != 200)) {
        return;
      }
      const { records } = res.payload;
      if (!records || records.length === 0) {
        showNotification("No categories found. Please add category first");
        return;
      }
      const tempCategoryOptions = records.map((item) => ({
        label: item.name,
        value: item.id,
      }));

      setCategoryOptions(tempCategoryOptions);
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Failed to load categories");
    }
  }

  async function getRecipeList(params) {
    setRecipeLoading(true);
    if (!params) return;
    if (!params.hasOwnProperty("restaurantId")) {
      console.error("Restaurant id is required for getting recipe list.");
      return;
    }
    if (!params.hasOwnProperty("recipeType")) {
      console.error("Recipe type is required for getting recipe list.");
      return;
    }
    try {
      const res = await ApiService.GET_INVENTORY_RECIPE_LIST(params, user.token.token);
      if (res.code !== 200) {
        showNotification(res.data.message);
        setRecipeLoading(false);
        return;
      }
      const { payload } = res;
      const { page, perPage, total, records } = payload;
      setPage(page);
      setPerPage(perPage);
      setTotal(total);
      setRecipes(records);
      setRecipeLoading(false);
    } catch (err) {
      setRecipeLoading(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  function onHandleViewItem(_id, recipeTypeLabel) {
    const recipeType = INVENTORY_RECIPE_TYPE.find((item) => item.label === recipeTypeLabel).value;
    return navigate(`/inventory/recipe/${_id}/details?recipeType=${recipeType}`);
  }

  function onHandleEditItem(_id, recipeTypeLabel, variantName) {
    // const { option } = recipes.filter((item) => item.id === _id)?.find((item) => item?.option?.name === variantName);

    const recipeType = INVENTORY_RECIPE_TYPE.find((item) => item.label === recipeTypeLabel).value;
    return navigate(`/inventory/recipe/${_id}/edit?recipeType=${recipeType}`);
  }

  function onHandleDeleteItem(recipeId, recipeType) {
    if (role === ROLES.STAFF) {
      showNotification("You are not authorize to delete this ingredient.");
      return;
    }

    setSelectedRecipeDeleteObj({ recipeId, recipeType: getRecipeTypeValue(recipeType) });
    setOpenDelModal(true);
  }

  function onHandleActiveItem(currentRecipeId, currentRecipeType, currentStatus) {
    setOpenStatusConfirmModal(true);
    setChangeStatusObj({
      recipeType: getRecipeTypeValue(currentRecipeType),
      recipeId: currentRecipeId,
      optionId: "",
      status: currentStatus === "Active" ? "Inactive" : "Active",
    });
  }

  function onHandleSearchText(e) {
    const val = e.target.value;
    setSearchText(val);
  }

  function removeRecipeTypeParamsFromUrl() {
    if (recipeTypeFromParams) {
      return navigate(location.pathname, { replace: true });
    }
  }

  function onHandleRecipeType(val) {
    removeRecipeTypeParamsFromUrl();
    setRecipeType(val);
  }

  function onHandleCategory(val) {
    setCategory(val);
  }

  function onHandleSearch() {
    removeRecipeTypeParamsFromUrl();
    setPage(1);
    setPerPage(10);
    const tempFilter = {
      restaurantId: restaurantId,
      recipeType: recipeType || "FOOD",
      searchKey: searchText.trim(),
      categoryId: category || "",
      status: "",
      page: 1,
      perPage: 10,
    };

    getRecipeList(tempFilter);
  }

  function onHandleAdd() {
    return navigate("/inventory/recipe/add");
  }

  function clearFilterFields() {
    setSearchText("");
    // value: FOOD
    setRecipeType(INVENTORY_RECIPE_TYPE[0].value);
    setCategory();
    setPage(1);
    setPerPage(10);
  }

  function onHandleClear() {
    removeRecipeTypeParamsFromUrl();
    clearFilterFields();
    refreshList({ restaurantId, recipeType: INVENTORY_RECIPE_TYPE[0].value });
  }

  function refreshList(params = null) {
    if (params !== null) {
      getRecipeList(params);
      return;
    }
    const filter = {
      restaurantId,
      searchKey: searchText.trim() || "",
      recipeType: recipeType,
      categoryId: category || "",
      page: page,
      perPage: perPage,
    };
    removeRecipeTypeParamsFromUrl();
    getRecipeList(filter);
  }

  // delete api
  async function deleteRecipe(params) {
    if (!params) return;
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_RECIPE_DELETE(params, user.token.token);
      if (!res || res.code !== 200) {
        setNetworkCall(false);
        setSelectedRecipeDeleteObj({});
        showNotification(res.data.message);
        return;
      }
      const { payload } = res;
      const { success, dependentFoods, dependentSetMenus, dependentOrders } = payload || {};
      if (success === false) {
        setOpenDelModal(false);
        setNetworkCall(false);
        setDependencyObj({ dependentFoods, dependentSetMenus, dependentOrders });
        setOpenDelDependencyModal(true);
        return;
      }

      setOpenDelModal(false);
      setNetworkCall(false);
      setSelectedRecipeDeleteObj({});
      showNotification("Recipe deleted successfully.", false);
      refreshList();
      return;
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  // delete with dependency
  async function deleteRecipeWithDependencyConfirmation(params) {
    if (!params) return;
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_RECIPE_DELETE_CONFIRM(params, user.token.token);
      if (!res || res.code !== 200) {
        setNetworkCall(false);
        showNotification(res.data.message);
        setSelectedRecipeDeleteObj({});
        setDependencyObj({});
        setOpenDelDependencyModal(false);
        return;
      }
      setSelectedRecipeDeleteObj({});
      setDependencyObj({});
      setOpenDelDependencyModal(false);
      setNetworkCall(false);
      refreshList();
      showNotification("Recipe deleted with dependency successfully.", false);
      return true;
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response.data.message);
      return false;
    }
  }

  // dependency modal confirm button handler
  async function onHandleDeleteConfirmWithDependency() {
    if (
      dependencyObj?.dependentOrders?.length > 0 &&
      dependencyObj?.dependentFoods?.length < 1 &&
      dependencyObj?.dependentSetMenus?.length < 1
    ) {
      showNotification("Sorry We can not delete the recipe forcefully for order dependency.");
      setOpenDelDependencyModal(false);
      setSelectedRecipeDeleteObj({});
      setDependencyObj({});
      return;
    }
    const params = {
      recipeType: selectedRecipeDeleteObj.recipeType,
      recipeId: selectedRecipeDeleteObj.recipeId,
      optionId: "",
      dependentFoods: [...dependencyObj.dependentFoods.map((item) => item.id)],
      dependentSetMenus: [...dependencyObj.dependentSetMenus.map((item) => item.id)],
      dependentOrders: [...dependencyObj.dependentOrders.map((item) => item.id)],
    };
    await deleteRecipeWithDependencyConfirmation(params);
  }

  // delete modal confirm handler
  function onHandleDeleteRecipeConfirm() {
    const DELETE_PAYLOAD = {
      recipeId: selectedRecipeDeleteObj.recipeId,
      recipeType: selectedRecipeDeleteObj.recipeType,
      optionId: "",
    };
    deleteRecipe(DELETE_PAYLOAD);
  }

  function closeConfirmationModal() {
    setSelectedRecipeDeleteObj({});
    setOpenDelModal(false);
  }

  async function changeRecipeStatus(params) {
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_RECIPE_STATUS_CHANGE(params, user.token.token);
      if (!res || res.code !== 200) {
        setNetworkCall(false);
        showNotification(res.message);
        changeStatusObj({});
        return;
      }
      const { payload } = res;
      const { success, dependentFoods, dependentSetMenus, dependentOrders } = payload;

      // When success is false
      if (!success) {
        setNetworkCall(false);
        setOpenStatusDependencyModal(true);
        setDependencyObj({ dependentFoods, dependentSetMenus, dependentOrders });
        return;
      }
      showNotification(`Variant ${(changeStatusObj.status).toLowerCase()} successfully!`, false);
      setChangeStatusObj({});
      setNetworkCall(false);
      refreshList();
      return;
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response?.message);
      return;
    }
  }

  async function confirmChangeRecipeStatus(params) {
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_RECIPE_STATUS_CHANGE_CONFIRM(params, user.token.token);
      if (!res || res.code !== 200) {
        setOpenStatusDependencyModal(false);
        setNetworkCall(false);
        showNotification(res.message);
        setChangeStatusObj({});
        return;
      }
      showNotification(`Variant ${(changeStatusObj.status).toLowerCase()} successfully!`, false);
      setOpenStatusDependencyModal(false);
      setChangeStatusObj({});
      setNetworkCall(false);
      refreshList();
      return;
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response?.data?.message);
      return false;
    }
  }

  async function onHandleStatusChangeRecipe() {
    setOpenStatusConfirmModal(false);
    changeRecipeStatus(changeStatusObj);
  }

  function closeStatusChangeModal() {
    setOpenStatusConfirmModal(false);
  }

  function onHandleConfirmChangeStatusForcefully() {
    if (
      dependencyObj.dependentOrders.length > 0 &&
      dependencyObj.dependentFoods.length < 1 &&
      dependencyObj.dependentSetMenus.length < 1
    ) {
      showNotification("Sorry We can not change the recipe status for order dependency.");
      setOpenStatusDependencyModal(false);
      setDependencyObj({});
      return;
    }
    const params = {
      recipeType: changeStatusObj.recipeType,
      recipeId: changeStatusObj.recipeId,
      optionId: changeStatusObj.optionId || "",
      dependentFoods: [...dependencyObj.dependentFoods.map((item) => item.id)],
      dependentSetMenus: [...dependencyObj.dependentSetMenus.map((item) => item.id)],
      dependentOrders: [...dependencyObj.dependentOrders.map((item) => item.id)],
    };
    confirmChangeRecipeStatus(params);
  }

  useEffect(() => {
    getCategories();
    const params = {
      restaurantId,
      recipeType: recipeType,
      page: 1,
      perPage: 10,
    };
    getRecipeList(params);

    return () => {
      setCategoryOptions([]);
    };
  }, [restaurantId]);

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chains`} menu={constants.MENU.INVENTORY} currentMenuKey={"T_7_S_7_L_5"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.INVENTORY} />
        <div className="flex flex-row p-[24px] w-full h-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <h1 className="pt-[26px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
              Recipe List
            </h1>
            {/* filter section */}
            <ConfigProvider
              theme={{
                components: {
                  Select: {
                    zIndexPopup: 1,
                  },
                },
              }}
            >
              <div className="mb-[20px] flex items-center justify-between">
                <div className="flex items-center space-x-[20px]">
                  <div className="w-[230px] h-[48px]">
                    <Input
                      name="search"
                      className={"w-full h-full"}
                      value={searchText}
                      onChange={onHandleSearchText}
                      onPressEnter={onHandleSearch}
                      placeholder="Search keyword"
                    />
                  </div>

                  <div className="w-[181px] h-[48px]">
                    <Select
                      value={recipeType}
                      name="recipeType"
                      style={{ width: "100%", height: "100%" }}
                      onChange={onHandleRecipeType}
                      options={INVENTORY_RECIPE_TYPE}
                      placeholder={"Recipe type"}
                    />
                  </div>
                  <div className="w-[312px] h-[48px]">
                    <Select
                      showSearch
                      value={category}
                      name="category"
                      style={{ width: "100%", height: "100%" }}
                      onChange={onHandleCategory}
                      options={categoryOptions}
                      placeholder="Select Category"
                      optionFilterProp="label"
                      filterSort={(optionA, optionB) =>
                        (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                      }
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      className="w-[100px] h-[48px] bg-black rounded-[5px] text-white font-bold leading-[16px]"
                      onClick={onHandleClear}
                    >
                      Clear
                    </button>
                    <button
                      className="w-[100px] h-[48px] bg-[#FCBD10] rounded-[5px] text-black font-bold leading-[16px]"
                      onClick={onHandleSearch}
                    >
                      Search
                    </button>
                  </div>
                </div>

                <button
                  className="w-[104px] h-[48px] flex items-center space-x-[19.2px] bg-[#FCBD10] rounded-[5px] pl-[25px] pr-[15.2px]"
                  onClick={onHandleAdd}
                >
                  <span className="font-bold text-black leading-[17px]">Add</span>
                  <Image
                    src="https://tabletap-cdn.s3.ap-south-1.amazonaws.com/tabletap-prod/static-assets/add.svg"
                    preview={false}
                    alt="add"
                  />
                </button>
              </div>
            </ConfigProvider>
            {/* filter section ends */}
            <div className="min-h-[90vh]">
              {recipeLoading ? (
                <div className="h-[50vh] flex items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : Array.isArray(recipes) && recipes.length < 1 ? (
                <div className="h-[50vh] flex items-center justify-center">
                  <Empty />
                </div>
              ) : (
                <div>
                  <InventoryTable
                    data={prepareRecipeList(recipes)}
                    coloredCols={["SN", "Recipe Type", "Total Cost", "Profit", "Selling Price", "Action"]}
                    handleView={onHandleViewItem}
                    handleEdit={onHandleEditItem}
                    handleDelete={onHandleDeleteItem}
                    handleAcitve={onHandleActiveItem}
                    showActiveButton={true}
                    showViewButton={true}
                    showEditButton={true}
                    paginationRecords={{
                      handlePageChange: onHandlePageChange,
                      page: page,
                      perPage: perPage,
                      setPerPage: setPerPage,
                      total: total,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <InventoryConfirmModal
        open={openDelModal}
        title="Are you sure?"
        message="This will remove the recipe permanently."
        onConfirm={onHandleDeleteRecipeConfirm}
        onCancel={closeConfirmationModal}
      />
      <InventoryConfirmModal
        open={openStatusConfirmModal}
        title="Are you sure?"
        message="This will change the status of the recipe"
        onConfirm={onHandleStatusChangeRecipe}
        onCancel={closeStatusChangeModal}
      />
      {/* status change dependency model */}
      {(dependencyObj?.dependentFoods?.length > 0 ||
        dependencyObj?.dependentSetMenus?.length > 0 ||
        dependencyObj?.dependentOrders?.length > 0) && (
        <InventoryDependencyModal
          open={openStatusDependencyModal}
          title="Are you sure want to change the recipe status forcefully!"
          dependentFoods={dependencyObj.dependentFoods}
          dependentSetMenus={dependencyObj.dependentSetMenus}
          dependentOrders={dependencyObj.dependentOrders}
          handleConfirm={onHandleConfirmChangeStatusForcefully}
          handleCancel={() => setOpenStatusDependencyModal(false)}
        />
      )}
      {/* recipe delete dependency model */}
      {(dependencyObj?.dependentFoods?.length > 0 ||
        dependencyObj?.dependentSetMenus?.length > 0 ||
        dependencyObj?.dependentOrders?.length > 0) && (
        <InventoryDependencyModal
          open={openDelDependencyModal}
          title="This recipe has some dependencies listed below. Are you sure want to delete it?"
          dependentFoods={dependencyObj.dependentFoods}
          dependentSetMenus={dependencyObj.dependentSetMenus}
          dependentOrders={dependencyObj.dependentOrders}
          handleConfirm={onHandleDeleteConfirmWithDependency}
          handleCancel={() => setOpenDelDependencyModal(false)}
        />
      )}
      {contextHolder}
    </div>
  );
}
